import { useId, useMemo, type CSSProperties, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isPlainLeftClick } from '../../utils/linkTarget'
import BlockIcon from './BlockIcon'
import { isStaffOnly, isWideBlock, pickActiveTab, TAB_PARAM, tabBlocks, tabSearch, tabsOf, type HomeSection } from './homeBlocks'

interface Props {
	/** Секция-контейнер `tabs`: заголовок и список вкладок. */
	section: HomeSection
	/** Вся выдача страницы: блоки вкладок отбираются из неё по разделу `tab:<слаг>`. */
	sections: HomeSection[]
	/** Рисует блок вкладки по его типу; null — типа нет или блок пуст. */
	renderBlock: (section: HomeSection) => ReactNode
	/** Стафф-превью (?preview=1): видны скрытые вкладки/блоки и пустые вкладки. */
	isPreview?: boolean
}

/** Доп. навигация главной: строка вкладок и содержимое активной вкладки.
 *
 *  Вкладка живёт в адресе (`/?tab=business`): ссылкой можно поделиться, средний клик открывает её
 *  в новой вкладке браузера; у первой вкладки адрес чистый — он же канонический. Блоки всех вкладок уже
 *  приехали одним ответом, поэтому переключение мгновенное; в DOM рисуется только активная вкладка —
 *  снимок пререндера и запасной index.html не раздуваются. Пилюли — те же, что в навигации разделов
 *  (.snav__item--l1): один язык вкладок на весь сайт. */
export default function HomeTabs({ section, sections, renderBlock, isPreview = false }: Props) {
	const location = useLocation()
	const navigate = useNavigate()
	const headingId = useId()

	// Вкладка без блоков клиенту не нужна; сотруднику в превью показываем и её — чтобы было видно, что наполнять.
	const tabs = useMemo(
		() => tabsOf(section).filter((t) => isPreview || tabBlocks(sections, t.slug).length > 0),
		[section, sections, isPreview],
	)
	const requested = new URLSearchParams(location.search).get(TAB_PARAM)
	const active = pickActiveTab(tabs, requested)
	if (!active) return null

	const blocks = tabBlocks(sections, active)
	const title = typeof section.config?.title === 'string' ? section.config.title : ''
	const subtitle = typeof section.config?.subtitle === 'string' ? section.config.subtitle : ''

	return (
		<section className="ht" aria-labelledby={title ? headingId : undefined} aria-label={title ? undefined : 'Разделы главной'}>
			{/* Сервер отдаёт такой контейнер только сотруднику — пометка, чтобы не решить, будто его видят клиенты. */}
			{isStaffOnly(section) && <div className="ht__staff-tag">вкладки главной видят только сотрудники</div>}
			{title && (
				<header className="ht__head">
					<h2 id={headingId} className="ht__title">{title}</h2>
					{subtitle && <p className="ht__subtitle">{subtitle}</p>}
				</header>
			)}
			{tabs.length > 1 && (
				<nav className="ht__row" aria-label="Вкладки главной">
					{tabs.map((tab, idx) => {
						const search = tabSearch(location.search, tabs, tab.slug)
						const isActive = tab.slug === active
						return (
							<a
								key={tab.slug}
								href={`${location.pathname}${search}`}
								className={`snav__item snav__item--l1 ht__tab${isActive ? ' is-active' : ''}${tab.is_visible === false ? ' is-hidden-tab' : ''}${tab.staff_only ? ' is-staff-tab' : ''}`}
								aria-current={isActive ? 'page' : undefined}
								title={tab.staff_only ? 'Вкладку видят только сотрудники' : undefined}
								style={{ '--item-idx': idx } as CSSProperties}
								onClick={(e) => {
									if (!isPlainLeftClick(e)) return
									e.preventDefault()
									// replace: «назад» уводит с главной, а не перебирает вкладки; скролл остаётся на месте.
									navigate({ pathname: location.pathname, search }, { replace: true, state: location.state })
								}}
							>
								<BlockIcon icon={tab.icon} className="ht__icon" size={16} />
								<span className="snav__label">{tab.label}</span>
							</a>
						)
					})}
				</nav>
			)}
			<div className="ht__content" key={active}>
				{/* Колонку страницы (1200px) держит сама обёртка блока — блоку «во всю ширину» она её не ставит. */}
				{blocks.map((block) => (
					<div
						key={block.id}
						className={`ht__block${isWideBlock(block) ? ' ht__block--wide' : ''}${isPreview && block.is_visible === false ? ' bots-feed-blocks__hidden' : ''}`}
					>
						{isPreview && block.is_visible === false && (
							<div className="bots-feed-blocks__hidden-tag">скрыт — видите только вы (превью)</div>
						)}
						{block.is_visible !== false && isStaffOnly(block) && !isStaffOnly(section) && (
							<div className="ht__staff-tag">блок видят только сотрудники</div>
						)}
						{renderBlock(block)}
					</div>
				))}
				{isPreview && blocks.length === 0 && <p className="ht__empty">Во вкладке пока нет блоков — добавьте их в админке.</p>}
			</div>
		</section>
	)
}
